import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/query-client.js';

import { DetailedShell } from './components/DetailedShell.js';
import { AriaRouterProvider } from './components/shell/AriaRouterProvider.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { useAuthStore } from './store/index.js';
import { clearAuth, refreshSession } from './services/api/session.js';
import { Spinner } from './components/tailgrids/core/spinner/index.js';

import { LoginPage } from './pages/Login.js';
import { SignupPage } from './pages/Signup.js';
import { InviteAcceptPage } from './pages/InviteAccept.js';
import { ResetPasswordPage } from './pages/ResetPassword.js';
import { VerifyEmailPage } from './pages/VerifyEmail.js';
import { HomeSurface } from './pages/simple/HomeSurface.js';
import { loginPath } from './lib/login-redirect.js';

// ==========================================
// Auth Gate — redirects to /login if not authenticated
// Waits for Zustand store rehydration before deciding.
// ==========================================

const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated());

  useEffect(() => {
    if (hydrated) return;
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    return unsub;
  }, [hydrated]);

  // Wait for Zustand to rehydrate from localStorage before making auth decisions
  if (!hydrated) return null;

  // Read auth state AFTER hydration is confirmed
  return <AuthGateInner>{children}</AuthGateInner>;
};

/**
 * Separated so auth selectors only run after hydration. The access token is memory-only, so after a reload a
 * signed-in tab has `isAuthenticated` but no token: it restores the session from the httpOnly refresh cookie
 * before rendering, and signs out when the cookie is gone or refused.
 */
const AuthGateInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const token = useAuthStore((s) => s.token);
  const restoring = isAuthenticated && !token;
  const [unreachable, setUnreachable] = useState(false);

  // Refused → sign out. Unreachable (server restarting, network blip) → keep the session and retry with
  // backoff, up to every 30 s, until the server answers.
  useEffect(() => {
    if (!restoring) return;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const restore = () => {
      refreshSession().then(
        (session) => {
          if (!cancelled && !session) clearAuth();
        },
        () => {
          if (cancelled) return;
          setUnreachable(true);
          attempt += 1;
          timer = setTimeout(restore, Math.min(30_000, 1_000 * 2 ** attempt));
        },
      );
    };
    restore();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [restoring]);

  if (!isAuthenticated) return <Navigate to={loginPath(location)} replace />;
  if (restoring) return <SessionRestoring unreachable={unreachable} />;
  return <>{children}</>;
};

function SessionRestoring({ unreachable }: { unreachable: boolean }) {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-3 bg-bg"
      role="status"
      aria-live="polite"
    >
      <Spinner size="md" className="text-accent" />
      {unreachable ? (
        <p className="text-sm text-muted">Can't reach the server. Reconnecting…</p>
      ) : (
        <span className="sr-only">Restoring your session…</span>
      )}
    </div>
  );
}

// ==========================================
// App Component
// ==========================================

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <BrowserRouter>
          <AriaRouterProvider>
            <Routes>
              {/* Auth routes - no layout, no auth required */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/invite/accept" element={<InviteAcceptPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />

              {/* Landing - simplified rail + conversation, or the detailed shell per settings.uiMode */}
              <Route
                path="/"
                element={
                  <AuthGate>
                    <HomeSurface />
                  </AuthGate>
                }
              />

              {/* App routes - auth required, with layout */}
              <Route
                path="/*"
                element={
                  <AuthGate>
                    <DetailedShell />
                  </AuthGate>
                }
              />
            </Routes>
          </AriaRouterProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
